import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, api } from '../../lib/api.ts'
import { safe } from '../../lib/prefs.ts'
import type { AyatSummary, Stats } from '../home/today.ts'

/** Mirrors `GET /api/me/ayat/:day` (apps/api/src/services/ayat.ts, apps/api/src/quran.ts). */

export type Word = { ar: string; arti: string | null; latin: string | null; audio: string | null }

export type Verse = {
  k: number
  ar: string
  latin: string | null
  /** Kemenag translation; "N)" marks a footnote explained in `footnotes` */
  terjemah: string
  footnotes: string | null
  kata: Word[] | null
  /** Tafsir Ringkas */
  wajiz: string | null
  audio: string | null
}

export type TemaGroup = { dari: number; sampai: number; judul?: string; munasabah?: string; kosakata?: string; sababNuzul?: string[]; kesimpulan?: string[] }
export type Tafsir = { pengantar: string | null; tema: TemaGroup[]; tahlili: { k: number; text: string | null }[] }

export type AyatDetail = {
  ok: true
  ayat: AyatSummary & {
    /** null when the Qur'an dataset is not installed: show the reference only */
    verses: Verse[] | null
    tafsir: Tafsir | null
    /** 1-based index of today's key word in the first verse */
    kataKunci: number | null
    catatanKata: string | null
    makna: string
    penerapan: string
    amalan: string
  }
  journal: { text: string; amalanDone: boolean; completedAt: string | null } | null
  /** the open day (can be completed); false = history */
  isCurrent: boolean
}

export type CompleteResponse = { ok: true; firstTime: boolean; stats: Stats }

export const STEPS = [
  { id: 'iqra', label: 'Iqra’' },
  { id: 'fahami', label: 'Fahami' },
  { id: 'renungi', label: 'Renungi' },
  { id: 'amalkan', label: 'Amalkan' },
] as const
export type StepId = (typeof STEPS)[number]['id']
export const isStep = (s: string | undefined): s is StepId => STEPS.some((x) => x.id === s)

export const ayatKey = (day: number) => ['ayat', day] as const
export const fetchAyat = (day: number) => api<AyatDetail>('GET', `/api/me/ayat/${day}`)
export const useAyat = (day: number) => useQuery({ queryKey: ayatKey(day), queryFn: () => fetchAyat(day) })

export const putJournal = (day: number, patch: { text?: string; amalanDone?: boolean }) => api<{ ok: true }>('PUT', `/api/me/ayat/${day}/journal`, patch)
export const completeAyat = (day: number, words: number) => api<CompleteResponse>('POST', `/api/me/ayat/${day}/complete`, { words })

const AR_DIGITS = '٠١٢٣٤٥٦٧٨٩'
export const arNum = (n: number) => String(n).replace(/\d/g, (d) => AR_DIGITS[Number(d)]!)

export const wordCount = (verses: Verse[] | null) => verses?.reduce((n, v) => n + (v.kata?.length ?? 0), 0) ?? 0

/** Device-local set of strings (steps visited, words opened). Progress hints only, never needed for correctness. */
export function useLocalSet(key: string) {
  const [items, setItems] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(safe.get(key) ?? '[]') as string[]) } catch { return new Set() }
  })
  const add = useCallback((v: string) => {
    setItems((prev) => {
      if (prev.has(v)) return prev
      const next = new Set(prev).add(v)
      safe.set(key, JSON.stringify([...next]))
      return next
    })
  }, [key])
  return [items, add] as const
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Journal autosave: debounced 700 ms, saves run one after another (never out of order), pending text is flushed on
 * blur, when the step unmounts and when the tab is hidden; the cached detail is patched so coming back shows what was typed.
 */
export function useJournal(day: number, initial: { text: string; amalanDone: boolean }) {
  const qc = useQueryClient()
  const [text, setTextState] = useState(initial.text)
  const [amalanDone, setAmalan] = useState(initial.amalanDone)
  const [status, setStatus] = useState<SaveStatus>('idle')
  const pending = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const chain = useRef<Promise<unknown>>(Promise.resolve())
  const alive = useRef(true)

  const patchCache = useCallback((p: { text?: string; amalanDone?: boolean }) => {
    qc.setQueryData<AyatDetail>(ayatKey(day), (d) => d && { ...d, journal: { text: '', amalanDone: false, completedAt: null, ...d.journal, ...p } })
  }, [qc, day])

  const send = useCallback((p: { text?: string; amalanDone?: boolean }) => {
    const run = chain.current.then(async () => {
      if (alive.current) setStatus('saving')
      // Patch first: coming back to this step must show what was typed even while (or if) the request is still out.
      patchCache(p)
      try {
        await putJournal(day, p)
        if (alive.current && pending.current === null) setStatus('saved')
        return true
      } catch {
        if (alive.current) setStatus('error')
        return false
      }
    })
    chain.current = run
    return run
  }, [day, patchCache])

  const flush = useCallback(() => {
    clearTimeout(timer.current)
    const v = pending.current
    if (v === null) return chain.current
    pending.current = null
    return send({ text: v })
  }, [send])

  const setText = (v: string) => {
    setTextState(v)
    pending.current = v
    clearTimeout(timer.current)
    timer.current = setTimeout(flush, 700)
  }

  const toggleAmalan = async (v: boolean) => {
    setAmalan(v)
    if (!(await send({ amalanDone: v })) && alive.current) setAmalan(!v)
  }

  /** Retry after "Gagal menyimpan": resend the current text. */
  const retry = () => { pending.current = text; return flush() }

  useEffect(() => {
    alive.current = true
    const onHide = () => { if (document.visibilityState === 'hidden') void flush() }
    document.addEventListener('visibilitychange', onHide)
    return () => { document.removeEventListener('visibilitychange', onHide); alive.current = false; void flush() }
  }, [flush])

  return { text, setText, amalanDone, toggleAmalan, status, flush, retry }
}

export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : 'Terjadi masalah. Coba lagi nanti.')
